import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  getFundTint,
  getPillarMotif,
  pillarLabel,
} from '../visuals/portalImagery';

type Variant = 'pillar' | 'thumb' | 'hero';

type Props = {
  pillar: string;
  slug?: string;
  image?: string;
  alt: string;
  variant?: Variant;
  className?: string;
  label?: string;
};

const sizeClass: Record<Variant, string> = {
  pillar: 'h-36 w-full md:h-40',
  thumb: 'h-24 w-32 shrink-0 sm:h-28 sm:w-40',
  hero: 'h-48 w-full md:h-64',
};

function Placeholder({
  pillar,
  slug,
  variant,
  label,
}: {
  pillar: string;
  slug?: string;
  variant: Variant;
  label?: string;
}) {
  const motif = getPillarMotif(pillar);
  const tint = slug ? getFundTint(slug) : undefined;
  const caption =
    label ||
    (variant === 'pillar' ? pillarLabel[pillar] || pillar : undefined);

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ background: motif.base }}
      aria-hidden={caption ? undefined : true}
      role={caption ? 'img' : undefined}
      aria-label={caption}
    >
      <div
        className="absolute inset-0 opacity-90 animate-slow-pan origin-center"
        style={{
          backgroundImage: motif.pattern,
          backgroundSize: variant === 'thumb' ? '64px 64px' : '100px 100px',
        }}
      />
      {tint && (
        <div className="absolute inset-0" style={{ background: tint }} />
      )}
      <div
        className="pointer-events-none absolute -right-8 top-1/3 h-40 w-40 rounded-full blur-2xl"
        style={{ background: motif.accent, opacity: 0.18 }}
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink-950/40 to-transparent" />
      {caption && variant === 'pillar' && (
        <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
          <p className="text-xs uppercase tracking-[0.22em] text-mist-50/90">
            {caption}
          </p>
        </div>
      )}
    </div>
  );
}

function useResolvedImageSrc(image?: string) {
  const [src, setSrc] = useState<string>('');

  useEffect(() => {
    let objectUrl = '';
    let cancelled = false;

    async function load() {
      if (!image) {
        setSrc('');
        return;
      }
      // Public static/remote images
      if (!image.includes('/api/')) {
        setSrc(image);
        return;
      }
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(image, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('cover fetch failed');
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(objectUrl);
      } catch {
        if (!cancelled) setSrc('');
      }
    }

    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [image]);

  return src;
}

export default function CoverVisual({
  pillar,
  slug,
  image,
  alt,
  variant = 'thumb',
  className,
  label,
}: Props) {
  const resolved = useResolvedImageSrc(image);
  const caption =
    label ||
    (variant === 'pillar' ? pillarLabel[pillar] || pillar : undefined);

  return (
    <div
      className={twMerge(
        'relative overflow-hidden bg-ink-900',
        sizeClass[variant],
        className,
      )}
    >
      {resolved ? (
        <>
          <img
            src={resolved}
            alt={alt}
            className="h-full w-full object-cover animate-fade-in"
          />
          {caption && variant === 'pillar' && (
            <>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink-950/70 via-ink-950/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                <p className="text-xs uppercase tracking-[0.22em] text-mist-50/95">
                  {caption}
                </p>
              </div>
            </>
          )}
        </>
      ) : (
        <Placeholder
          pillar={pillar}
          slug={slug}
          variant={variant}
          label={label}
        />
      )}
    </div>
  );
}
